import React, { useState, useEffect } from 'react';
import { searchMeals } from '../utils/api';
import RecipeCard from './RecipeCard';
import RecipeModal from './RecipeModal';
import { Search, X, Loader2 } from 'lucide-react';

function SearchOverlay({ isOpen, onClose, onFavoriteChange }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState(null);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    useEffect(() => {
        const searchRecipes = async () => {
            if (!query.trim()) {
                setResults([]);
                return;
            }

            setLoading(true);
            try {
                const searchResults = await searchMeals(query);
                setResults(searchResults);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(searchRecipes, 300); // Debounce search
        return () => clearTimeout(timeoutId);
    }, [query]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleViewDetails = (recipe) => {
        setSelectedRecipe(recipe);
    };

    const handleCloseModal = () => {
        setSelectedRecipe(null);
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="search-overlay" onClick={handleBackdropClick}>
                <div className="search-container">
                    <div className="search-header">
                        <Search className="search-header-icon" />
                        <h2>Search Recipes</h2>
                        <button
                            className="close-btn"
                            onClick={onClose}
                            aria-label="Close search"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="search-input-container">
                        <Search className="search-input-icon" size={18} />
                        <input
                            type="text"
                            placeholder="Search for recipes..."
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="search-input"
                            autoFocus
                        />
                    </div>

                    <div className="search-results">
                        {loading ? (
                            <div className="loading">
                                <Loader2 className="loading-icon" />
                                Searching recipes...
                            </div>
                        ) : query && results.length === 0 ? (
                            <div className="no-results">
                                No recipes found for "{query}"
                            </div>
                        ) : (
                            <div className="recipes-grid">
                                {results.map(recipe => (
                                    <RecipeCard
                                        key={recipe.idMeal}
                                        recipe={recipe}
                                        onViewDetails={handleViewDetails}
                                        onFavoriteChange={onFavoriteChange}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {selectedRecipe && (
                <RecipeModal
                    recipe={selectedRecipe}
                    onClose={handleCloseModal}
                    onFavoriteChange={onFavoriteChange}
                />
            )}
        </>
    );
}

export default SearchOverlay;